import { Component, OnDestroy, inject, signal, computed } from '@angular/core';
import { NgClass } from '@angular/common';
import {
  MatDialogRef,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { jobStatusData } from '../../models/vendor-order.model';
import { ScrollService } from '../../services/scroll.service';

/** Possible dialog status types */
export type JobStatusType =
  | 'uploading'
  | 'uploaded'
  | 'progress'
  | 'complete'
  | 'fail';

/**
 * Dialog component that shows real-time job status feedback during
 * quote request submission. Mirrors the legacy JobStatusModalComponent
 * behaviour using Angular Material Dialog and signals.
 *
 * Flow: uploading → uploaded → progress (polling) → complete | fail
 */
@Component({
  selector: 'app-job-status-dialog',
  standalone: true,
  imports: [NgClass, MatDialogContent, MatDialogActions, MatButtonModule],
  templateUrl: './job-status-dialog.component.html',
  styleUrl: './job-status-dialog.component.scss',
})
export class JobStatusDialogComponent implements OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<JobStatusDialogComponent>);
  private readonly router = inject(Router);
  private readonly scrollService = inject(ScrollService);

  /** Current status type */
  readonly statusType = signal<JobStatusType>('uploading');

  /** Whether this is a DTP (Desktop Publishing) job */
  readonly isDtpJob = signal(false);

  /** Whether this is a Canadian user submission */
  readonly isCanadian = signal(false);

  /** Whether this is an ad-hoc service request */
  readonly isAdhocService = signal(false);

  /** Files that failed during processing */
  readonly failedFiles = signal<any[]>([]);

  /** Whether user can go back to upload more */
  readonly showUploadMore = signal(false);

  /** Custom error message override (used when setErrorState is called with a message) */
  readonly customErrorMessage = signal<string | null>(null);

  /** Current status message computed from statusType and flags */
  readonly statusMessage = computed(() => {
    const type = this.statusType();
    const isDtp = this.isDtpJob();
    const isCanada = this.isCanadian();
    const isAdhoc = this.isAdhocService();

    if (type === 'uploading') {
      return 'Uploading files…';
    }

    if (isAdhoc && type === 'uploaded') {
      return 'Files uploaded as special request.';
    }

    if (isCanada && (type === 'uploaded' || type === 'progress')) {
      return 'Files uploaded, processing to send to linguistics team.';
    }

    switch (type) {
      case 'uploaded':
        return isDtp
          ? 'Files uploaded. Your DTP job is being processed – estimates will appear on the View Quotes page shortly.'
          : 'Files uploaded, processing estimates.';
      case 'progress':
        return 'Estimates are being retrieved. Your progress will be saved if you would like to return later.';
      case 'complete':
        return 'The intake form has been sent to AZ Translate vendors. Please expect a quote in the next 2-3 business days.';
      case 'fail':
        return (
          this.customErrorMessage() ??
          'We were unable to submit your quote. Please try again.'
        );
      default:
        return '';
    }
  });

  /** CSS class for the dialog wrapper based on current state */
  readonly dialogStateClass = computed(() => {
    const type = this.statusType();
    if (type === 'complete') return 'state-complete';
    if (type === 'fail') return 'state-fail';
    return 'state-loading';
  });

  /** Whether to show the spinner animation */
  readonly showSpinner = computed(() => {
    const type = this.statusType();
    return type === 'uploading' || type === 'uploaded' || type === 'progress';
  });

  /** Whether to show the success icon */
  readonly showSuccessIcon = computed(() => {
    const type = this.statusType();
    return type === 'complete';
  });

  /** Whether to show the error icon */
  readonly showErrorIcon = computed(() => {
    const type = this.statusType();
    return type === 'fail';
  });

  /** Whether to show the "View Quotes" link */
  readonly showViewQuotesLink = computed(() => {
    return this.statusType() === 'complete';
  });

  private autoCloseTimeout: ReturnType<typeof setTimeout> | null = null;

  // ── Public API (called by the parent component) ───────────────────────

  /**
   * Transition to the success state after files have been uploaded and verified.
   * Called by the parent component once the backend responds and S3 uploads succeed.
   */
  setJobStatusInterval(jobDetails: jobStatusData): void {
    const { isCanadian, isDtpJob, isAdhocService } = jobDetails;

    this.isCanadian.set(!!isCanadian);
    this.isDtpJob.set(!!isDtpJob);
    this.isAdhocService.set(!!isAdhocService);

    if (isAdhocService) {
      this.statusType.set('uploaded');
      this.scheduleAutoClose(5000);
      return;
    }

    // Upload is done — show success immediately
    this.statusType.set('complete');
  }

  /**
   * Mark the dialog as failed due to an external error (e.g., S3 upload failure).
   */
  setErrorState(message?: string): void {
    if (message) {
      this.customErrorMessage.set(message);
    }
    this.statusType.set('fail');
  }

  // ── Dialog actions ────────────────────────────────────────────────────

  closeDialog(): void {
    this.dialogRef.close(this.failedFiles());
  }

  /** Navigate back to the intake form to create another quote */
  requestNewQuote(): void {
    this.dialogRef.close(this.failedFiles());
    this.router.navigate(['/vendor-translation']).then(() => {
      this.scrollService.scrollToTop();
    });
  }

  /** Navigate to the Translation Manager page */
  navigateToTranslationManager(): void {
    this.dialogRef.close(this.failedFiles());
    this.router.navigate(['/translation-manager']);
  }

  // ── Cleanup ───────────────────────────────────────────────────────────

  private scheduleAutoClose(delay: number): void {
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
    }
    this.autoCloseTimeout = setTimeout(() => {
      this.closeDialog();
    }, delay);
  }

  ngOnDestroy(): void {
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
    }
  }
}
